// Biçimlendirme yardımcıları — panelin her yerinde aynı gösterim.
// Tasarımdaki (Call Agent Admin.dc.html) davranış birebir korunmuştur.

import type { DateRange, ReportPreset } from './types';

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** Verilen IANA dilimindeki BUGÜN, "YYYY-MM-DD" (en-CA biçimi ISO verir).
 *  Tarayıcının yerel saati KULLANILMAZ: panelin günü marka dilimidir
 *  (constants.tzOfBrands — Vegas/Prime=İstanbul, BetBooker=Akra). */
export function todayInTz(tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

/** ISO an → "HH:MM" **marka dilimine göre** (BetBooker=Akra, diğerleri=İstanbul).
 *
 *  `todayInTz` ile aynı gerekçe: tarayıcının yerel saati KULLANILMAZ. Kartın diğer
 *  saatleri (İlk–Son, Terminal) sunucuda `hhmmInTz(x, tzOfBrands(brands))` ile
 *  üretiliyor; istemcide `toLocaleTimeString`'i dilimsiz çağırmak aynı kartta
 *  Akra ve İstanbul saatlerini yan yana koyar — BetBooker için 3 saat sapma.
 *  Sunucu eşi: src/engine/istanbul.js `hhmmInTz`. */
export function fmtClock(iso: string | number | Date | null | undefined, tz: string, withSeconds = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: tz, hour: '2-digit', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}), hour12: false,
  }).format(d);
}

/** ISO an → "GG.AA SS:DD" (yıl istenirse "GG.AA.YYYY SS:DD") **marka dilimine göre**.
 *  `fmtClock`'un gün+ay taşıyan kardeşi; uyarı/senkron damgaları için. Çıktı tamamen
 *  sayısal ⇒ EN kaplamasının çevirecek bir şeyi yok, sözlük girdisi gerekmez. */
export function fmtStamp(iso: string | number | Date | null | undefined, tz: string, withYear = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: tz, day: '2-digit', month: '2-digit',
    ...(withYear ? { year: 'numeric' } : {}),
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d).replace(', ', ' ');
}

// Saf "YYYY-MM-DD" takvim aritmetiği (saat dilimi karışmaz) —
// sunucu eşi: src/engine/istanbul.js addDays/weekStart/monthStart.
export const addDays = (dayStr: string, n: number): string => {
  const [y, m, d] = dayStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
/** Bir günün ait olduğu haftanın PAZARTESİ'si. Haftalık Rapor sayfası tarih
 *  kutusundan gelen her günü bununla kıstırır — sunucu Pazartesi olmayan bir
 *  `week` parametresini 400 `not_monday` ile reddeder. */
export const weekStartOf = (dayStr: string): string => {
  const [y, m, d] = dayStr.split('-').map(Number);
  const wd = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Pzt=0
  return addDays(dayStr, -wd);
};
const monthStartOf = (dayStr: string): string => dayStr.slice(0, 8) + '01';

/** "YYYY-MM-DD" → "DD.MM.YYYY"; ayrıştırılamazsa girdiyi aynen döndürür. */
export function fmtDate(iso: string): string {
  if (!iso) return '';
  const p = iso.split('-');
  return p.length === 3 ? p[2] + '.' + p[1] + '.' + p[0] : iso;
}

/** Eksik değer yer tutucusu YOKTUR: boş bir alan boş kalır, "—" basılmaz (sahibin
 *  kararı). Bu yüzden birleştirmeler ayıracı da düşürmek zorunda — aşağıdaki iki
 *  yardımcı bunun içindir, yoksa "Ad · " / " – 18:32" gibi sarkıntılar kalır. */

/** "09:21–18:32" — uçlardan biri yoksa var olan uç, ikisi de yoksa boş dizgi. */
export function fmtRange(a: string, b: string, sep = '–'): string {
  return a && b ? a + sep + b : a || b || '';
}

/** "Ad · Departman" — boş parçalar ayıraçlarıyla birlikte düşer. */
export function joinDot(...parts: (string | null | undefined)[]): string {
  return parts.filter((p) => p && p.trim()).join(' · ');
}

/** Göreli zaman: "az önce" / "12 dk önce" / "3 sa önce" / "2 gün önce". */
export function fmtRel(iso: string | null | undefined, now: number): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (s < 45) return 'az önce';
  const m = Math.floor(s / 60);
  if (m < 1) return 'az önce';
  if (m < 60) return m + ' dk önce';
  const h = Math.floor(m / 60);
  if (h < 24) return h + ' sa önce';
  return Math.floor(h / 24) + ' gün önce';
}

/** Geçen süre sayacı: "12:34" (<1sa) ya da "1sa 20dk". */
export function fmtElapsed(iso: string | null | undefined, now: number): string {
  if (!iso) return '0:00';
  const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  if (m < 60) return m + ':' + pad2(ss);
  return Math.floor(m / 60) + 'sa ' + (m % 60) + 'dk';
}

/** Saniye → "dk:sn" (bekleme süreleri metriği). */
export function fmtMS(sec: number): string {
  const v = Math.max(0, Math.round(sec || 0));
  return Math.floor(v / 60) + ':' + pad2(v % 60);
}

/** Saniye → "s:dd:ss" (≥1 saat) ya da "d:ss" — konuşma/çalma süreleri. Yalnız rakam + iki
 *  nokta: dil bağımsız, i18n sözlüğüne girmez. null/NaN → '' (§6: değer yoksa boş). */
export function fmtHMS(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '';
  const v = Math.max(0, Math.round(sec));
  const h = Math.floor(v / 3600);
  const m = Math.floor((v % 3600) / 60);
  return h ? h + ':' + pad2(m) + ':' + pad2(v % 60) : m + ':' + pad2(v % 60);
}

/** Saniye → "s:mm" (DARK konuşma süresi tablo hücresi). */
export function fmtTalk(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h + ':' + pad2(m);
}

/** Saniye → "1sa 24dk" (DARK karşılaştırma kartı; yalnız saat + dakika, saniye YOK). */
export function fmtTalkLong(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return (h ? h + 'sa ' : '') + m + 'dk';
}

/** Gün-içi dakika (0-1440) → "HH:MM". */
export const hm = (m: number) => pad2(Math.floor(m / 60) % 24) + ':' + pad2(m % 60);

// ── Haftalık Rapor: para + yüzde ─────────────────────────────────────────────
// İkisi de SAF SAYISAL çıktı üretir (ayıraçlar dahil) — EN kaplamasının çevirecek
// bir şeyi yoktur, sözlük girdisi gerekmez (fmtStamp ile aynı gerekçe).

const GROUPED_TR = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });

/** 1510472.75 → "1.510.472,75". PARA BİRİMİ SİMGESİ YOK: sheet'teki tutarın
 *  birimi sütuna göre değişir, uydurulmaz. null/NaN → boş dizge (§6: eksik değere
 *  yer tutucu basılmaz, alan boş kalır). */
export function fmtMoney(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? '' : GROUPED_TR.format(n);
}

/** 12.5 → "%12,5" (TR yazımı: yüzde işareti ÖNDE, ondalık virgül). null → boş. */
export function fmtPct1(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? '' : '%' + n.toFixed(1).replace('.', ',');
}

/** Dönem hızlı-seçimleri — MARKA dilimindeki "bugün"e demirler (tarayıcı saati
 *  DEĞİL; tz = constants.tzOfBrands(seçili markalar)). Hafta = Pzt–Paz, ay =
 *  1'i–sonu (PLAN.md kararı). Sunucu eşi: src/api/reports.js presetRange —
 *  takvim kurallarını senkron tut. */
export function computePreset(preset: ReportPreset, tz: string): DateRange {
  const today = todayInTz(tz);
  if (preset === 'yesterday') {
    const y = addDays(today, -1);
    return { start: y, end: y };
  }
  if (preset === 'this_week') {
    const mon = weekStartOf(today);
    return { start: mon, end: addDays(mon, 6) };
  }
  if (preset === 'last_week') {
    const mon = addDays(weekStartOf(today), -7);
    return { start: mon, end: addDays(mon, 6) };
  }
  if (preset === 'this_month') {
    const first = monthStartOf(today);
    return { start: first, end: addDays(monthStartOf(addDays(first, 31)), -1) };
  }
  return { start: today, end: today };  // 'today' (ve bilinmeyen değerler)
}

/**
 * CSV indir. BOM + CRLF-güvenli alıntılama: Excel'in Türkçe karakterleri
 * bozmaması için "﻿" ön eki zorunlu (repo kuralı: UTF-8, BOM'suz DOSYA —
 * ama indirilen CSV Excel için BOM'lu üretilir; bu bir kaynak dosya değildir).
 */
export function downloadCSV(filename: string, matrix: (string | number)[][]): void {
  const csv = matrix
    .map((row) =>
      row
        .map((c) => {
          const t = String(c == null ? '' : c);
          return /[",\n;]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
